import React, { Component } from "react";
import styled from "styled-components";
import { DialogTitle, DialogContent, Icon, IconButton } from "@material-ui/core";
import { Providers, ProviderState, SimpleProvider } from "@microsoft/mgt-element";
import { FileList, MgtTemplateProps } from "@microsoft/mgt-react";
import type { DriveItem } from "@microsoft/microsoft-graph-types";

import { BeautifulDialog, ShowErrorDialog } from "./BaseDialog";
import Spinner from "../Spinner";
import { Gap10 } from "../Gap";

const DialogContentWrapper = styled(DialogContent)`
    margin-bottom: 20px;
    max-height: 500px;
    mgt-file-list {
        --font-family: "Source Sans Pro";
    }

    & .empty-state-text {
        font-weight: 400;
        font-size: 18px;
        color: #666;
        padding: 0px 0px 20px 12px;
    }

    & .current-folder {
        display: flex;
        align-items: center;
        padding: 0 0 20px;

        & .current-folder-card {
            display: flex;
            align-items: center;
            justify-content: flex-start;
            padding: 10px 15px 10px 10px;
            font-weight: 600;
            font-size: 14px;

            & .MuiIcon-root {
                opacity: 0.5;
            }
            & span {
                margin-left: 10px;
            }
        }        
    }
`;

interface Props {
    token: string,
    fileExtensions: string[],
    handleSelectedFile: Function,
    closeDialog: Function
}

interface State {
    filterFolders: DriveItem[],
    isPickerReady: boolean
}

export default class OneDriveFilePickerDialog extends Component<Props, State> {
    fileListRef = React.createRef<{ reload: (clearCache?: boolean) => void }>();

    state = {
        filterFolders: [],
        isPickerReady: false
    }

    componentDidMount() {
        const { token } = this.props;

        if (token) {
            Providers.globalProvider = new SimpleProvider(scopes => Promise.resolve(token));
            Providers.globalProvider.setState(ProviderState.SignedIn);

            this.setState({ isPickerReady: true });
        }
    }

    componentDidUpdate(_prevProps: Props, prevState: State) {
        if (!prevState.isPickerReady && this.state.isPickerReady) {
            this.fileListRef.current?.reload(true);
        }
    }

    handleSelectedFile = event => {
        const { fileExtensions, handleSelectedFile, closeDialog } = this.props;

        const driveItem = event.detail as DriveItem;
        if (driveItem.folder) {
            this.setState({ filterFolders: [driveItem, ...this.state.filterFolders] });
            return;
        }

        const fileExtension = /(?:\.([^.]+))?$/.exec(driveItem.name)?.[1]?.toLowerCase();
        const normalizedFileExtensions = Array.from(new Set(fileExtensions.map(extension => extension.toLowerCase())));
        if (fileExtension && normalizedFileExtensions.includes(fileExtension)) {
            handleSelectedFile(driveItem);
            closeDialog();
            return;
        }

        const formattedFileExtensions = normalizedFileExtensions
            .filter(extension => !extension.includes("/"))
            .map(extension => extension.startsWith(".") ? extension : `.${extension}`);
        const allowedFileTypes = formattedFileExtensions.length > 1
            ? `${formattedFileExtensions.slice(0, -1).join(", ")} or ${formattedFileExtensions[formattedFileExtensions.length - 1]}`
            : formattedFileExtensions[0];

        ShowErrorDialog({
            title: "Unsupported file type",
            message: allowedFileTypes
                ? `Please select a ${allowedFileTypes} file from OneDrive.`
                : "Please select a supported file from OneDrive.",
        });
    }

    render() {
        const { closeDialog } = this.props;
        const { isPickerReady, filterFolders } = this.state;

        return (
            <BeautifulDialog closeButton closeDialog={closeDialog}>
                <DialogTitle>OneDrive File Picker</DialogTitle>
                <DialogContentWrapper>
                    {!isPickerReady && <Spinner />}
                    {isPickerReady && (
                        <>
                            {filterFolders.length ? (
                                <div className="current-folder">
                                    <IconButton size="small" onMouseUp={() => this.setState({ filterFolders: filterFolders.slice(0, -1) })}>
                                        <Icon>arrow_backward</Icon>
                                    </IconButton>
                                    <div className="current-folder-card">
                                        <Icon>folder_open</Icon>
                                        <Gap10 />
                                        {filterFolders[0].name}
                                        <span>({filterFolders[0].folder?.childCount ?? 0})</span>
                                    </div>
                                </div>
                            ) : <></>}
                            <FileList ref={this.fileListRef} itemId={filterFolders[0]?.id} itemClick={this.handleSelectedFile}>
                                <EmptyStateTemplate template="no-data" />
                            </FileList>
                        </>
                    )}
                </DialogContentWrapper>
            </BeautifulDialog>
        );
    }
}

class EmptyStateTemplate extends Component<MgtTemplateProps> {
    render() {
        return <div className="empty-state-text">No files found</div>;
    }
}
